import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Trash2, AlertTriangle, Pause, Play } from 'lucide-react';

/*
  Records a short voice message for a ticket.

  Two browser constraints shape this component:

  1. getUserMedia only exists in a *secure context* — HTTPS, or localhost. Over
     plain HTTP `navigator.mediaDevices` is undefined, so instead of failing at
     click time we detect it up front and explain why, pointing at the HTTPS
     address. See isSecure below.
  2. MediaRecorder output differs per browser: Chrome/Firefox produce
     webm/opus, Safari mp4/aac. We ask for the first type the browser admits to
     supporting and let the server accept any of them, rather than assuming webm.

  The parent owns the result: `onChange` receives { blob, url, mimeType,
  durationSeconds } or null when the clip is discarded.
*/

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
  '',                       // let the browser pick (Safari)
];

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  return MIME_CANDIDATES.find(
    (t) => t === '' || (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)),
  ) ?? '';
};

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export default function VoiceRecorder({ onChange, maxSeconds = 180, disabled = false }) {
  const [supported, setSupported] = useState(true);
  const [reason,    setReason]    = useState('');
  const [recording, setRecording] = useState(false);
  const [paused,    setPaused]    = useState(false);
  const [seconds,   setSeconds]   = useState(0);
  const [clip,      setClip]      = useState(null);   // { url, durationSeconds }
  const [error,     setError]     = useState('');

  const recorderRef = useRef(null);
  const chunksRef   = useRef([]);
  const timerRef    = useRef(null);
  const streamRef   = useRef(null);
  const clipUrlRef  = useRef(null);

  useEffect(() => {
    // window.isSecureContext covers https:// and localhost, which is exactly
    // the condition the browser applies to the microphone.
    const isSecure = typeof window !== 'undefined' && window.isSecureContext;
    if (!isSecure) {
      setSupported(false);
      setReason('Recording needs a secure (HTTPS) connection.');
    } else if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setSupported(false);
      setReason('This browser does not support audio recording.');
    }
  }, []);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Always stop the mic and revoke the preview URL, even if the user navigates
  // away mid-recording — otherwise the tab keeps showing the recording dot.
  useEffect(() => () => {
    clearInterval(timerRef.current);
    releaseStream();
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
  }, [releaseStream]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    clearInterval(timerRef.current);
    setRecording(false);
    setPaused(false);
  }, []);

  const start = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];

      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = rec.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        const url  = URL.createObjectURL(blob);
        if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
        clipUrlRef.current = url;
        // setSeconds is async, so read the counter off the ref-free closure by
        // using the functional form and reporting from there.
        setSeconds((secs) => {
          setClip({ url, durationSeconds: secs });
          onChange?.({ blob, url, mimeType: type, durationSeconds: secs });
          return secs;
        });
        releaseStream();
      };

      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      setPaused(false);
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= maxSeconds) { stop(); return maxSeconds; }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      // Most common cause by far is the user denying the permission prompt.
      const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
      setError(denied
        ? 'Microphone permission was blocked. Allow it in your browser’s address bar and try again.'
        : `Could not start recording: ${e?.message || e?.name || 'unknown error'}`);
      releaseStream();
      setRecording(false);
    }
  };

  const togglePause = () => {
    const rec = recorderRef.current;
    if (!rec) return;
    if (rec.state === 'recording') {
      rec.pause(); setPaused(true); clearInterval(timerRef.current);
    } else if (rec.state === 'paused') {
      rec.resume(); setPaused(false);
      timerRef.current = setInterval(() => {
        setSeconds((s) => { if (s + 1 >= maxSeconds) { stop(); return maxSeconds; } return s + 1; });
      }, 1000);
    }
  };

  const discard = () => {
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    clipUrlRef.current = null;
    setClip(null);
    setSeconds(0);
    onChange?.(null);
  };

  const box = {
    border: '1px solid var(--border, #e5e7eb)', borderRadius: 10,
    padding: '14px 16px', background: 'var(--bg-subtle, #f8fafc)',
  };

  if (!supported) {
    const httpsUrl = typeof window !== 'undefined'
      ? window.location.href.replace(/^http:/, 'https:').replace(/:5051|:5050/, '')
      : '';
    return (
      <div style={{ ...box, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text-main)' }}>Voice recording unavailable.</strong> {reason}
          {reason.includes('HTTPS') && (
            <> Open the ticket form over HTTPS to record a message
              {httpsUrl && <> — try <code>{httpsUrl}</code></>}.
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={box}>
      {!clip ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {!recording ? (
            <button type="button" className="btn btn-outline btn-sm" onClick={start} disabled={disabled}
              style={{ color: '#b91c1c', borderColor: '#fca5a5' }}>
              <Mic size={14} /> Record voice message
            </button>
          ) : (
            <>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>
                <span style={{
                  width: 10, height: 10, borderRadius: '50%', background: '#dc2626',
                  animation: paused ? 'none' : 'vr-pulse 1.2s ease-in-out infinite',
                }} />
                {paused ? 'Paused' : 'Recording'} · {fmt(seconds)}
              </span>
              <button type="button" className="btn btn-outline btn-sm" onClick={togglePause}>
                {paused ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
              </button>
              <button type="button" className="btn btn-danger btn-sm" onClick={stop}>
                <Square size={13} /> Stop
              </button>
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>max {fmt(maxSeconds)}</span>
            </>
          )}
          {!recording && (
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              Optional — describe the issue in your own words.
            </span>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <audio src={clip.url} controls style={{ height: 36, maxWidth: '100%' }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmt(clip.durationSeconds)}</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={discard}
            style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
            <Trash2 size={13} /> Discard &amp; re-record
          </button>
        </div>
      )}
      {error && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: '#b91c1c' }}>{error}</div>
      )}
      <style>{`@keyframes vr-pulse{0%,100%{opacity:1}50%{opacity:.25}}`}</style>
    </div>
  );
}
